import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  computed,
  inject,
  signal,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  UntypedFormArray,
  UntypedFormControl,
} from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatListModule, MatSelectionList } from '@angular/material/list';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { ConditionConfigurationModel } from '@fleet/model';
import { Subject, Subscription } from 'rxjs';

import { ToggleWithLeftLabelAndDescriptionComponent } from '@fleet/ui/components/toggles/toggle-with-left-label-and-description/toggle-with-left-label-and-description.component';
import { NetworkConditionsService } from '../network-conditions.service';

export interface NetworkConditionAssignmentGroup {
  label: string;
  assignmentMode: 'PREFERENCE' | 'ATTRIBUTE';
  conditions: ConditionConfigurationModel[];
}

/**
 * Network conditions CVA.
 *
 * Parent value in: a condition id string, id[], or objects with `networkConditionId`.
 * Parent value out: id[] (or the raw inner control value, which is id[] in select/list).
 *
 * - `list`: option value is `networkConditionId`.
 * - `select`: option value is `networkConditionId` (must match writeValue ids).
 * - `switch` / `toggle`: inner FormArray of booleans; writeValue maps ids to checked.
 *
 * Configs load asynchronously; `syncConditionsAndFormArray` re-applies writeValue
 * after the current filter's configs arrive.
 */
@Component({
  selector: 'fleet-network-conditions-selector',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatSelectModule,
    MatOptionModule,
    MatListModule,
    MatProgressSpinnerModule,
    ToggleWithLeftLabelAndDescriptionComponent,
  ],
  templateUrl: './network-conditions-selector.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => NetworkConditionsSelectorComponent),
      multi: true,
    },
  ],
})
export class NetworkConditionsSelectorComponent
  implements OnInit, OnChanges, ControlValueAccessor, OnDestroy
{
  @Input() conditionsLabel: string = null;
  inSearchForm = input(false);
  lockedConditionIds = input<string[]>([]);
  /** When true, list mode groups BOOKING conditions by assignmentMode. Default keeps a flat list. */
  groupByAssignmentMode = input(false);
  driverConditions: ConditionConfigurationModel[] = [];
  vehicleConditions: ConditionConfigurationModel[] = [];
  travellerConditions: ConditionConfigurationModel[] = [];
  jobConditions: ConditionConfigurationModel[] = [];
  organisationConditions: ConditionConfigurationModel[] = [];
  medallionConditions: ConditionConfigurationModel[] = [];

  allConditions: ConditionConfigurationModel[] = [];

  currentEntityConditionIds: string[] = [];

  conditionsFormArray: UntypedFormArray = new UntypedFormArray([]);

  _unsubscribeAll: Subject<any> = new Subject();

  /** Value-change subscriptions for `mode === 'toggle'` (no template `(change)` on fleet toggle). */
  private toggleModeSubs = new Subscription();

  @Input() filter: string;
  formControl = new UntypedFormControl();
  @Input() mode: string;
  @Input() valueMode: string;
  @Input() columns = '2';
  @Input() multiSelect = true;

  @Output() addCondition = new EventEmitter();
  @Output() removeCondition = new EventEmitter();
  @ViewChild('conditionSelectionList') conditionSelectionList: MatSelectionList;

  selectedConditions: ConditionConfigurationModel[] = [];
  /** True once condition configs for the current `filter` have been loaded from the service. */
  conditionsLoaded = signal(false);
  /** Filter types whose condition config HTTP response has been applied (e.g. DRIVER, VEHICLE). */
  resolvedFilters = signal<Set<string>>(new Set());
  /** Network condition IDs with an in-flight add/remove API call from the parent. */
  processingConditionIds = signal<Set<string>>(new Set());
  private selectedConditionIds = signal<string[]>([]);
  /** Bumped when condition config arrays are replaced so assignment groups recompute. */
  private conditionsRevision = signal(0);

  assignmentModeGroups = computed(() => {
    this.conditionsRevision();
    return this.buildAssignmentModeGroups();
  });

  private networkConditionsService = inject(NetworkConditionsService);
  public changeDetectorRef = inject(ChangeDetectorRef);

  ngOnInit(): void {
    this.networkConditionsService.driverConditions.subscribe(
      (c: ConditionConfigurationModel[]) => {
        if (this.driverConditions.length === 0 && c) {
          this.driverConditions = c;
          this.bumpConditionsRevision();
          this.resolvedFilters.update((s) => new Set(s).add('DRIVER'));
          this.syncConditionsAndFormArray();
          this.markConditionsLoadedForFilter('DRIVER');
          this.changeDetectorRef.markForCheck();
        }
      }
    );

    this.networkConditionsService.vehicleConditions.subscribe(
      (c: ConditionConfigurationModel[]) => {
        if (this.vehicleConditions.length === 0 && c) {
          this.vehicleConditions = c;
          this.bumpConditionsRevision();
          this.resolvedFilters.update((s) => new Set(s).add('VEHICLE'));
          this.syncConditionsAndFormArray();
          this.markConditionsLoadedForFilter('VEHICLE');
          this.changeDetectorRef.markForCheck();
        }
      }
    );

    this.networkConditionsService.travellerConditions.subscribe(
      (c: ConditionConfigurationModel[]) => {
        if (this.travellerConditions.length === 0 && c) {
          this.travellerConditions = c;
          this.bumpConditionsRevision();
          this.resolvedFilters.update((s) => new Set(s).add('TRAVELLER'));
          this.syncConditionsAndFormArray();
          this.markConditionsLoadedForFilter('TRAVELLER');
          this.changeDetectorRef.markForCheck();
        }
      }
    );

    this.networkConditionsService.jobConditions.subscribe(
      (c: ConditionConfigurationModel[]) => {
        if (this.jobConditions.length === 0 && c) {
          this.jobConditions = c;
          this.bumpConditionsRevision();
          this.resolvedFilters.update((s) => new Set(s).add('BOOKING'));
          this.syncConditionsAndFormArray();
          this.markConditionsLoadedForFilter('BOOKING');
          this.changeDetectorRef.markForCheck();
        }
      }
    );

    this.networkConditionsService.organisationConditions.subscribe(
      (c: ConditionConfigurationModel[]) => {
        if (this.organisationConditions.length === 0 && c) {
          this.organisationConditions = c;
          this.bumpConditionsRevision();
          this.resolvedFilters.update((s) => new Set(s).add('ORGANISATION'));
          this.syncConditionsAndFormArray();
          this.markConditionsLoadedForFilter('ORGANISATION');
          this.changeDetectorRef.markForCheck();
        }
      }
    );

    this.networkConditionsService.medallionConditions.subscribe(
      (c: ConditionConfigurationModel[]) => {
        if (this.medallionConditions.length === 0 && c) {
          this.medallionConditions = c;
          this.bumpConditionsRevision();
          this.resolvedFilters.update((s) => new Set(s).add('MEDALLION'));
          this.syncConditionsAndFormArray();
          this.markConditionsLoadedForFilter('MEDALLION');
          this.changeDetectorRef.markForCheck();
        }
      }
    );

    this.formControl.valueChanges.subscribe((value: string[] | null) => {
      //this should onchnage array of ids
      //used in job, driver ,vehicle search and manula dispatch

      //you can check that write value is working in search context by running
      //WhenCreatingJobWithConditions and then opening manual dispath at job/dispatch/{{jobId}}
      //the job.jobDetails.conditions needs to be mapped to array of ids and form control value in manual dispatch needs to be patch
      this.selectedConditionIds.set(this.normalizeConditionIds(value));
      this.onTouched();
      this.onChange(value);
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['filter']) {
      if (!changes['filter'].firstChange) {
        this.conditionsLoaded.set(false);
      }
      this.syncConditionsLoadedWithFilter();
    }
    if (changes['mode']) {
      if (this.mode === 'toggle') {
        this.refreshToggleModeSubscriptions();
      } else {
        this.toggleModeSubs.unsubscribe();
        this.toggleModeSubs = new Subscription();
      }
    }
  }

  /** If `filter` is bound after configs already arrived, still mark loaded. */
  private syncConditionsLoadedWithFilter(): void {
    if (!this.filter) {
      return;
    }
    if (this.resolvedFilters().has(this.filter)) {
      this.conditionsLoaded.set(true);
    }
  }

  markConditionProcessing(networkConditionId: string): void {
    this.processingConditionIds.update((s) => {
      const n = new Set(s);
      n.add(networkConditionId);
      return n;
    });
  }

  clearConditionProcessing(networkConditionId: string): void {
    this.processingConditionIds.update((s) => {
      const n = new Set(s);
      n.delete(networkConditionId);
      return n;
    });
  }

  isConditionProcessing(networkConditionId: string): boolean {
    return this.processingConditionIds().has(networkConditionId);
  }

  onChange: any = () => undefined;
  onTouched: any = () => undefined;
  registerOnChange(fn: any) {
    this.onChange = fn;
  }
  registerOnTouched(fn: any) {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) {
      this.formControl.disable({ emitEvent: false });
    } else {
      this.formControl.enable({ emitEvent: false });
    }
  }

  /**
   * Accepts a single id, id[], or `{ networkConditionId }[]`.
   * Inner select/list controls and switch FormArray checks use the normalized id[].
   */
  writeValue(conditionIds: string[] | string | { networkConditionId?: string }[] | null) {
    if (conditionIds) {
      const ids = this.normalizeConditionIds(conditionIds);
      this.currentEntityConditionIds = ids;

      this.formControl.setValue(ids, {
        emitEvent: false,
      });
      this.selectedConditionIds.set(ids);

      if (this.filter === 'DRIVER') {
        this.conditionsFormArray.setValue(
          this.driverConditions.map(
            (condition: ConditionConfigurationModel) => {
              if (ids.includes(condition.networkConditionId)) {
                return true;
              } else {
                return false;
              }
            }
          ),
          { emitEvent: false }
        );
      } else if (this.filter === 'VEHICLE') {
        this.conditionsFormArray.setValue(
          this.vehicleConditions.map(
            (condition: ConditionConfigurationModel) => {
              if (ids.includes(condition.networkConditionId)) {
                return true;
              } else {
                return false;
              }
            }
          ),
          { emitEvent: false }
        );

        this.changeDetectorRef.markForCheck();
        this.changeDetectorRef.detectChanges();
      } else if (this.filter === 'TRAVELLER') {
        this.conditionsFormArray.setValue(
          this.travellerConditions.map(
            (condition: ConditionConfigurationModel) => {
              if (ids.includes(condition.networkConditionId)) {
                return true;
              } else {
                return false;
              }
            }
          ),
          { emitEvent: false }
        );
      } else if (this.filter === 'BOOKING') {
        this.conditionsFormArray.setValue(
          this.jobConditions.map((condition: ConditionConfigurationModel) => {
            if (ids.includes(condition.networkConditionId)) {
              return true;
            } else {
              return false;
            }
          }),
          { emitEvent: false }
        );
      } else if (this.filter === 'ORGANISATION') {
        this.conditionsFormArray.setValue(
          this.organisationConditions.map(
            (condition: ConditionConfigurationModel) => {
              if (ids.includes(condition.networkConditionId)) {
                return true;
              } else {
                return false;
              }
            }
          ),
          { emitEvent: false }
        );
        this.changeDetectorRef.markForCheck();
        this.changeDetectorRef.detectChanges();
      } else if (this.filter === 'MEDALLION') {
        this.conditionsFormArray.setValue(
          this.medallionConditions.map(
            (condition: ConditionConfigurationModel) => {
              if (ids.includes(condition.networkConditionId)) {
                return true;
              } else {
                return false;
              }
            }
          ),
          { emitEvent: false }
        );

        this.changeDetectorRef.markForCheck();
        this.changeDetectorRef.detectChanges();
      } else {
        this.conditionsFormArray.setValue(
          this.allConditions.map((condition: ConditionConfigurationModel) => {
            if (ids.includes(condition.networkConditionId)) {
              return true;
            } else {
              return false;
            }
          }),
          { emitEvent: false }
        );
      }
    }
  }
  ngOnDestroy(): void {
    this.toggleModeSubs.unsubscribe();
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  syncConditionsAndFormArray(): void {
    if (this.filter === 'DRIVER') {
      this.conditionsFormArray = new UntypedFormArray(
        this.driverConditions.map(
          (condition) =>
            new UntypedFormControl({
              value: null,
              disabled: condition.assignmentMode === 'CERTIFICATE',
            })
        )
      );
    } else if (this.filter === 'VEHICLE') {
      this.conditionsFormArray = new UntypedFormArray(
        this.vehicleConditions.map((condition) => new UntypedFormControl())
      );
    } else if (this.filter === 'TRAVELLER') {
      this.conditionsFormArray = new UntypedFormArray(
        this.travellerConditions.map((condition) => new UntypedFormControl())
      );
    } else if (this.filter === 'BOOKING') {
      this.conditionsFormArray = new UntypedFormArray(
        this.jobConditions.map((condition) => new UntypedFormControl())
      );
    } else if (this.filter === 'ORGANISATION') {
      this.conditionsFormArray = new UntypedFormArray(
        this.organisationConditions.map(
          (condition) =>
            new UntypedFormControl({
              value: null,
              disabled: condition.assignmentMode === 'CERTIFICATE',
            })
        )
      );
    } else if (this.filter === 'MEDALLION') {
      this.conditionsFormArray = new UntypedFormArray(
        this.medallionConditions.map((condition) => new UntypedFormControl())
      );
    }

    // Reapply entity selection if writeValue ran before configs were available.
    this.writeValue(this.currentEntityConditionIds);
    this.refreshToggleModeSubscriptions();
    this.changeDetectorRef.markForCheck();
  }

  private markConditionsLoadedForFilter(loadedType: string): void {
    if (this.filter === loadedType) {
      this.conditionsLoaded.set(true);
    }
  }

  toggleCondition(condition: ConditionConfigurationModel, checked: boolean) {
    if (!checked) {
      this.removeCondition.emit(condition.networkConditionId);
    } else {
      this.addCondition.emit(condition.networkConditionId);
    }
  }

  private getConditionsForCurrentFilter(): ConditionConfigurationModel[] {
    if (this.filter === 'VEHICLE') {
      return this.vehicleConditions;
    }
    if (this.filter === 'DRIVER') {
      return this.driverConditions;
    }
    if (this.filter === 'TRAVELLER') {
      return this.travellerConditions;
    }
    if (this.filter === 'BOOKING') {
      return this.jobConditions;
    }
    if (this.filter === 'ORGANISATION') {
      return this.organisationConditions;
    }
    if (this.filter === 'MEDALLION') {
      return this.medallionConditions;
    }
    return [];
  }

  private bumpConditionsRevision(): void {
    this.conditionsRevision.update((v) => v + 1);
  }

  private buildAssignmentModeGroups(): NetworkConditionAssignmentGroup[] {
    const conditions = this.getConditionsForCurrentFilter();
    const sortFn = (
      a: ConditionConfigurationModel,
      b: ConditionConfigurationModel
    ) => {
      const orderDiff = (a.displayOrder ?? 0) - (b.displayOrder ?? 0);
      if (orderDiff !== 0) {
        return orderDiff;
      }
      return (a.name ?? '').localeCompare(b.name ?? '');
    };

    const groups: NetworkConditionAssignmentGroup[] = [
      {
        label: 'Passenger Needs',
        assignmentMode: 'PREFERENCE',
        conditions: conditions
          .filter((c) => c.assignmentMode === 'PREFERENCE')
          .sort(sortFn),
      },
      {
        label: 'Job Conditions',
        assignmentMode: 'ATTRIBUTE',
        conditions: conditions
          .filter((c) => c.assignmentMode === 'ATTRIBUTE')
          .sort(sortFn),
      },
    ];

    return groups.filter((group) => group.conditions.length > 0);
  }

  isConditionSelected(networkConditionId: string): boolean {
    return this.selectedConditionIds().includes(networkConditionId);
  }

  isAssignmentGroupChecked(group: NetworkConditionAssignmentGroup): boolean {
    return (
      group.conditions.length > 0 &&
      group.conditions.every((c) =>
        this.selectedConditionIds().includes(c.networkConditionId)
      )
    );
  }

  isAssignmentGroupIndeterminate(
    group: NetworkConditionAssignmentGroup
  ): boolean {
    const selectedCount = group.conditions.filter((c) =>
      this.selectedConditionIds().includes(c.networkConditionId)
    ).length;
    return selectedCount > 0 && selectedCount < group.conditions.length;
  }

  toggleAssignmentGroup(
    group: NetworkConditionAssignmentGroup,
    checked: boolean
  ): void {
    const current = new Set(this.selectedConditionIds());
    if (checked) {
      group.conditions.forEach((c) => current.add(c.networkConditionId));
    } else {
      group.conditions.forEach((c) => current.delete(c.networkConditionId));
    }
    this.setSelectedConditionIds([...current]);
  }

  toggleConditionSelection(
    networkConditionId: string,
    checked: boolean
  ): void {
    const current = new Set(this.selectedConditionIds());
    if (checked) {
      current.add(networkConditionId);
    } else {
      current.delete(networkConditionId);
    }
    this.setSelectedConditionIds([...current]);
  }

  private setSelectedConditionIds(ids: string[]): void {
    this.selectedConditionIds.set(ids);
    this.currentEntityConditionIds = ids;
    this.formControl.setValue(ids.length > 0 ? ids : null);
    this.changeDetectorRef.markForCheck();
  }

  private normalizeConditionIds(value: unknown): string[] {
    if (!value) {
      return [];
    }
    if (typeof value === 'string') {
      return [value];
    }
    if (Array.isArray(value)) {
      return value.map((item) =>
        typeof item === 'string' ? item : item?.networkConditionId
      ).filter(Boolean);
    }
    return [];
  }

  private refreshToggleModeSubscriptions(): void {
    this.toggleModeSubs.unsubscribe();
    this.toggleModeSubs = new Subscription();

    if (this.mode !== 'toggle') {
      return;
    }

    const conditions = this.getConditionsForCurrentFilter();
    if (!conditions?.length || !this.conditionsFormArray?.length) {
      return;
    }

    conditions.forEach((condition, i) => {
      const ctrl = this.conditionsFormArray.at(i);
      if (!ctrl) {
        return;
      }
      const sub = ctrl.valueChanges.subscribe((checked: boolean) => {
        this.toggleCondition(condition, checked);
      });
      this.toggleModeSubs.add(sub);
    });
  }
}
